import { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Loader2, MousePointerClick, Satellite } from "lucide-react";

const pinIcon = L.divIcon({
  className: "",
  html: '<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:#F5B700;border:3px solid #0B0F17;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.4)"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

// Click sulla mappa = sposta il segnaposto sullo stabile giusto
const ClickToMove = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
};

const FollowPin = ({ pin }) => {
  const map = useMap();
  useEffect(() => {
    map.panTo([pin.lat, pin.lng]);
  }, [map, pin.lat, pin.lng]);
  return null;
};

const coordInput = "h-10 w-full border border-slate-700 bg-slate-900 px-3 text-sm text-white outline-none focus:border-emerald-500";

// Campo coordinata con testo libero: aggiorna il segnaposto solo quando il numero è valido
const CoordInput = ({ label, value, max, onValid, testId }) => {
  const [text, setText] = useState(value.toFixed(6));
  useEffect(() => {
    setText((t) => (Number(t) === value ? t : value.toFixed(6)));
  }, [value]);
  const change = (e) => {
    const t = e.target.value;
    setText(t);
    const n = Number(t);
    if (t.trim() !== "" && Number.isFinite(n) && Math.abs(n) <= max) onValid(n);
  };
  return (
    <label className="text-xs text-slate-400">{label}
      <input data-testid={testId} inputMode="decimal" className={`${coordInput} mt-1`} value={text} onChange={change} />
    </label>
  );
};

export const LocationPicker = ({ pin, setPin, onConfirm, onBack, loading }) => (
  <div data-testid="location-picker" className="fade-up rounded-3xl border border-slate-800 bg-[#111827] p-6 sm:p-8">
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-400"><Satellite className="h-4 w-4" /> Conferma lo stabile</p>
    <p className="mt-2 flex items-start gap-2 text-sm text-slate-300">
      <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0" /> Sposta la mappa e clicca sul tetto giusto (o trascina il segnaposto), poi conferma.
    </p>
    <div className="relative z-0 mt-5 h-80 overflow-hidden border border-slate-800 sm:h-96">
      <MapContainer center={[pin.lat, pin.lng]} zoom={19} maxZoom={21} className="h-full w-full" data-testid="location-map">
        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Immagini &copy; Esri" maxNativeZoom={19} maxZoom={21} />
        <Marker position={[pin.lat, pin.lng]} icon={pinIcon} draggable
          eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); setPin({ lat: p.lat, lng: p.lng }); } }} />
        <ClickToMove onPick={setPin} />
        <FollowPin pin={pin} />
      </MapContainer>
    </div>
    <div translate="no" className="mt-4 grid grid-cols-2 gap-3">
      <CoordInput label="Latitudine" testId="location-lat-input" value={pin.lat} max={90} onValid={(lat) => setPin({ ...pin, lat })} />
      <CoordInput label="Longitudine" testId="location-lng-input" value={pin.lng} max={180} onValid={(lng) => setPin({ ...pin, lng })} />
    </div>
    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
      <button data-testid="location-back-button" onClick={onBack} className="inline-flex items-center justify-center gap-2 border border-slate-700 px-5 py-3 text-sm font-medium text-slate-200 hover:border-slate-400">
        <ArrowLeft className="h-4 w-4" /> Modifica dati
      </button>
      <button data-testid="location-confirm-button" onClick={onConfirm} disabled={loading}
        className="inline-flex flex-1 items-center justify-center gap-2 bg-emerald-500 px-5 py-3 font-semibold text-slate-900 hover:bg-emerald-400 disabled:opacity-70">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analisi satellitare in corso…</> : "Conferma stabile e calcola"}
      </button>
    </div>
  </div>
);
